/**
 * Converts a string to a URL-friendly slug
 * @param text - The text to convert to a slug
 * @returns A URL-friendly slug
 */
export const createSlug = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    // Replace Turkish characters
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    // Replace spaces and special characters with hyphens
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    // Remove multiple consecutive hyphens
    .replace(/-+/g, '-')
    // Remove leading and trailing hyphens
    .replace(/^-+|-+$/g, '');
};

/**
 * Generates an event URL path from event title and ID
 * @param eventTitle - The event title (or eventTitle field)
 * @param eventID - The event ID
 * @returns A URL path like "/events/event-name-event-id"
 */
export const generateEventUrl = (eventTitle: string | undefined, eventID: string): string => {
  const slug = eventTitle ? createSlug(eventTitle) : 'event';
  return `/events/${slug}-${eventID}`;
};

/**
 * Generates an event share URL path from event ID
 * @param eventID - The event ID
 * @returns A URL path like "/share/event/{eventID}"
 */
export const generateEventShareUrl = (eventID: string): string => {
  return `/share/event/${eventID}`;
};

/**
 * Extracts the event ID from a URL path
 * @param path - The URL path (e.g., "/events/event-name-event-id" or "/events/event-name-event-id/tickets")
 * @returns The event ID or null if not found
 */
export const extractEventIdFromUrl = (path: string): string | null => {
  // Remove /tickets or /sponsorships if present
  const cleanPath = path.replace(/\/tickets$|\/sponsorships$/, '');
  
  // Match pattern: /events/anything-event-id
  // The event ID is the last segment after the last hyphen
  const match = cleanPath.match(/\/events\/(.+)$/);
  if (match && match[1]) {
    const eventPath = match[1];
    // Split by hyphen and take the last segment as the event ID
    const parts = eventPath.split('-');
    if (parts.length > 0) {
      return parts[parts.length - 1];
    }
  }
  return null;
};

/**
 * Generates a community URL path from community title and channel ID
 * @param communityTitle - The community title (or channelTitle field)
 * @param channelID - The channel ID
 * @returns A URL path like "/communities/community-name-channelID"
 */
export const generateCommunityUrl = (communityTitle: string | undefined, channelID: string): string => {
  const slug = communityTitle ? createSlug(communityTitle) : 'community';
  return `/communities/${slug}-${channelID}`;
};

/**
 * Generates a community share URL path from channel ID
 * @param channelID - The channel ID
 * @returns A URL path like "/share/community/{channelID}"
 */
export const generateCommunityShareUrl = (channelID: string): string => {
  return `/share/community/${channelID}`;
};

/**
 * Extracts the channel ID from a community URL path
 * @param path - The URL path (e.g., "/communities/community-name-channelID")
 * @returns The channel ID or null if not found
 */
export const extractChannelIdFromUrl = (path: string): string | null => {
  const match = path.match(/\/communities\/(.+)$/);
  if (match && match[1]) {
    const pathSegment = match[1];
    // Split by hyphen and take the last segment as the channel ID
    const parts = pathSegment.split('-');
    if (parts.length > 1) {
      // The last part should be the channel ID
      const channelID = parts[parts.length - 1];
      // Validate it looks like an ID (alphanumeric)
      if (/^[a-zA-Z0-9]+$/.test(channelID) && channelID.length > 0) {
        return channelID;
      }
    }
  }
  return null;
};

/**
 * Extracts the community slug from a URL path (for backward compatibility)
 * @param path - The URL path (e.g., "/communities/community-name-channelID")
 * @returns The community slug (without ID) or null if not found
 */
export const extractCommunitySlugFromUrl = (path: string): string | null => {
  const match = path.match(/\/communities\/(.+)$/);
  if (match && match[1]) {
    const pathSegment = match[1];
    // Remove the channel ID (last segment after last hyphen)
    const parts = pathSegment.split('-');
    if (parts.length > 1) {
      // Remove the last part (channel ID) and rejoin
      parts.pop();
      return parts.join('-');
    }
    // If no ID found, return the whole path (backward compatibility)
    return pathSegment;
  }
  return null;
};

/**
 * Generates an event admin URL path from event title, ID, and channel slug
 * @param eventTitle - The event title (or eventTitle field)
 * @param eventID - The event ID
 * @param channelSlug - The channel slug
 * @returns A URL path like "/communities/{channel-slug}/admin/{event-name}-{event-id}"
 */
export const generateEventAdminUrl = (eventTitle: string | undefined, eventID: string, channelSlug: string): string => {
  const slug = eventTitle ? createSlug(eventTitle) : 'event';
  return `/communities/${channelSlug}/admin/${slug}-${eventID}`;
};

/**
 * Checks if a path segment looks like an event path (event-name-event-id pattern)
 * Event paths must contain at least one hyphen and end with what looks like an ID
 * @param pathSegment - The path segment to check (e.g., "event-name-123" or "settings")
 * @returns true if it looks like an event path
 */
const isEventPathSegment = (pathSegment: string): boolean => {
  // Event paths must:
  // 1. Contain at least one hyphen
  // 2. Have multiple parts when split by hyphen (at least name and ID)
  // 3. Not be a known channel admin route
  const channelAdminRoutes = ['settings', 'members', 'mailing', 'revenue', 'events'];
  if (channelAdminRoutes.includes(pathSegment.toLowerCase())) {
    return false;
  }
  
  // Must have at least one hyphen (separating name from ID)
  if (!pathSegment.includes('-')) {
    return false;
  }
  
  // Split by hyphen - should have at least 2 parts (name and ID)
  const parts = pathSegment.split('-');
  if (parts.length < 2) {
    return false;
  }
  
  // Last part should look like an ID (alphanumeric, at least 1 character)
  const lastPart = parts[parts.length - 1];
  return /^[a-zA-Z0-9]+$/.test(lastPart) && lastPart.length > 0;
};

/**
 * Extracts the event ID from an admin URL path
 * @param path - The URL path (e.g., "/communities/{channel-slug}/admin/{event-name}-{event-id}" or "/communities/{channel-slug}/admin/{event-name}-{event-id}/speakers")
 * @returns The event ID or null if not found
 */
export const extractEventIdFromAdminUrl = (path: string): string | null => {
  // Match pattern: /communities/{channel-slug}/admin/{event-name}-{event-id} or with sub-route
  const match = path.match(/\/communities\/[^/]+\/admin\/([^/]+)/);
  if (match && match[1]) {
    const eventPath = match[1];
    
    // Only process if it looks like an event path
    if (!isEventPathSegment(eventPath)) {
      return null;
    }
    
    // Split by hyphen and take the last segment as the event ID
    const parts = eventPath.split('-');
    if (parts.length > 0) {
      return parts[parts.length - 1];
    }
  }
  return null;
};

/**
 * Extracts the event path (name-id) from an admin URL path
 * @param path - The URL path (e.g., "/communities/{channel-slug}/admin/{event-name}-{event-id}" or "/communities/{channel-slug}/admin/{event-name}-{event-id}/speakers")
 * @returns The event path (name-id) or null if not found
 */
export const extractEventPathFromAdminUrl = (path: string): string | null => {
  // Match pattern: /communities/{channel-slug}/admin/{event-name}-{event-id} (stop at first / to exclude sub-routes)
  const match = path.match(/\/communities\/[^/]+\/admin\/([^/]+)/);
  if (match && match[1]) {
    const pathSegment = match[1];
    
    // Only return if it looks like an event path (not a channel admin route)
    if (isEventPathSegment(pathSegment)) {
      return pathSegment;
    }
  }
  return null;
};

/**
 * Generates a user profile URL path from name, lastname, and user ID
 * @param name - User first name
 * @param lastname - User last name
 * @param userID - The user ID
 * @returns A URL path like "/profiles/name-lastname-userID"
 */
export const generateProfileUrl = (
  name: string | undefined,
  lastname: string | undefined,
  userID: string
): string => {
  const fullName = `${name || ''} ${lastname || ''}`.trim();
  const slug = fullName ? createSlug(fullName) : 'user';
  return `/profiles/${slug}-${userID}`;
};

/**
 * Extracts the user ID from a profile URL path
 * @param path - The URL path (e.g., "/profiles/name-lastname-userID" or just the userPath param)
 * @returns The user ID or null if not found
 */
export const extractUserIdFromProfileUrl = (path: string): string | null => {
  // Handle both full path and just the userPath param
  const segment = path.startsWith('/profiles/')
    ? path.replace('/profiles/', '')
    : path;

  if (!segment) return null;

  const parts = segment.split('-');
  if (parts.length > 0) {
    const userId = parts[parts.length - 1];
    if (/^[a-zA-Z0-9]+$/.test(userId) && userId.length > 0) {
      return userId;
    }
  }
  return null;
};

/**
 * Normalizes a LinkedIn URL to ensure it has the proper protocol and format
 * Handles various formats:
 * - https://linkedin.com/...
 * - http://linkedin.com/...
 * - https://www.linkedin.com/...
 * - http://www.linkedin.com/...
 * - www.linkedin.com/...
 * - linkedin.com/...
 * @param url - The LinkedIn URL to normalize
 * @returns A normalized LinkedIn URL with https:// protocol, or null if invalid
 */
export const normalizeLinkedInUrl = (url: string | null | undefined): string | null => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  // Trim whitespace
  const trimmedUrl = url.trim();
  
  if (!trimmedUrl) {
    return null;
  }

  // Remove any trailing slashes for consistency
  let cleanUrl = trimmedUrl.replace(/\/+$/, '');

  // Check if it's already a full URL with protocol
  if (/^https?:\/\//i.test(cleanUrl)) {
    // Already has protocol, just ensure it's https
    cleanUrl = cleanUrl.replace(/^http:\/\//i, 'https://');
    
    // Check if it's a LinkedIn URL
    if (/^https:\/\/(www\.)?linkedin\.com/i.test(cleanUrl)) {
      return cleanUrl;
    }
    // If it has a protocol but isn't LinkedIn, return as-is (might be a different URL)
    return cleanUrl;
  }

  // Check if it starts with www.linkedin.com or linkedin.com
  if (/^(www\.)?linkedin\.com/i.test(cleanUrl)) {
    // Add https:// protocol
    return `https://${cleanUrl}`;
  }

  // If it doesn't match any LinkedIn pattern, return null (invalid LinkedIn URL)
  return null;
};
